import { useBrand } from '@bnnvara/brand';
import { GridItem } from '@bnnvara/grids';
import { formatDate } from '@bnnvara/helpers';
import { Heading } from '@bnnvara/typography';
import ReviewRating from '@bnnvara/rating';
import PropTypes from 'prop-types';
import React from 'react';
import { BaseMetaBlock } from '../components/BaseMetaBlock/BaseMetaBlock';

import { Bookmark } from '../components/Bookmark/button/Bookmark';
import Link from '../components/Link';
import { getBrandSlug } from '../helpers/getBrandSlug';
import { parseArticleElements } from '../helpers/parseArticleElements';
import { getSlug } from '../helpers/slugify';

const formatReadingTime = (minutes) => {
  const notation = minutes === 1 ? 'minuut' : 'minuten';

  return `${minutes} ${notation}`;
};

export const ComposableArticleHeader = ({
  title,
  publishDate,
  readingTime,
  categories,
  viewCount,
  article,
  subThemes,
  review,
  persons,
  isPost,
}) => {
  const categoryList = categories.map((category, index) => (
    <Link
      href={
        isPost ? `community/${category.slug}` : `rubrieken/${category.slug}`
      }
      key={category.slug}
    >
      <a>
        {category.name}
        <span key={`comma-${category.name}`}>
          {index < categories.length - 1 ? ', ' : null}
        </span>
      </a>
    </Link>
  ));

  const brand = useBrand();
  const brandSlug = getBrandSlug(brand.slug);
  const bookmarkedHref = isPost
    ? `${brandSlug}/comunity/${getSlug(article.category.title)}/${
        article?.slug
      }`
    : `${brandSlug}/artikelen/${article?.slug}`;

  return (
    <GridItem
      columnStartSm="1"
      columnEndSm="span 12"
      columnStartMd="2"
      columnEndMd="span 10"
      columnStartLg="3"
      columnEndLg="11"
    >
      <Heading element="h1" variant="one">
        {title}
      </Heading>
      {review && (
        <ReviewRating
          review={{
            rating: review.rating,
            type: review.type,
            author: persons.length > 0 ? persons[0] : null,
            itemReviewed: review.productName,
            headline: title,
            publicationDate: publishDate
              ? formatDate(
                  new Date(publishDate),
                  'yyyy-MM-dd HH:mm:ssxx'
                ).replace(' ', 'T')
              : '',
            reviewBody: parseArticleElements(article.elements),
            image: article.thumbnail?.url?.replace('/{format}', ''),
          }}
        />
      )}
      <BaseMetaBlock
        subThemes={subThemes}
        categories={categoryList}
        publicationDate={
          publishDate ? formatDate(new Date(publishDate), 'dd-MM-yyyy') : ''
        }
        duration={
          readingTime ? `leestijd ${formatReadingTime(readingTime)}` : undefined
        }
        viewCount={viewCount ? parseInt(viewCount, 10) : 0}
        isPost={isPost}
      >
        {!isPost && (
          <Bookmark
            content={{
              id: article?.id,
              title: article?.title,
              type: isPost ? 'post' : 'article',
              publishDate: isPost ? article?.createdAt : article?.publishDate,
              href: bookmarkedHref,
              image: {
                url: article?.thumbnail?.url,
                altText: article?.title,
              },
            }}
          />
        )}
      </BaseMetaBlock>
    </GridItem>
  );
};

ComposableArticleHeader.defaultProps = {
  categories: [],
  isPost: false,
};

export const ReviewPropType = PropTypes.shape({
  rating: PropTypes.number,
  type: PropTypes.string,
  productName: PropTypes.string,
});

ComposableArticleHeader.propTypes = {
  title: PropTypes.string.isRequired,
  publishDate: PropTypes.string.isRequired,
  readingTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    })
  ),
  viewCount: PropTypes.string,
  article: PropTypes.shape(),
  subThemes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      slug: PropTypes.string,
      color: PropTypes.string,
      colorHover: PropTypes.string,
      colorActive: PropTypes.string,
      colorVisited: PropTypes.string,
      backgroundImageUrl: PropTypes.string,
      backgroundImageMobileUrl: PropTypes.string,
      parentSlug: PropTypes.string,
      parent: {
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        slug: PropTypes.string,
        color: PropTypes.string,
        colorHover: PropTypes.string,
        colorActive: PropTypes.string,
        colorVisited: PropTypes.string,
        backgroundImageUrl: PropTypes.string,
        backgroundImageMobileUrl: PropTypes.string,
      },
    })
  ),
  review: ReviewPropType,
  persons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
    })
  ).isRequired,
  isPost: PropTypes.bool,
};

export default ComposableArticleHeader;
